import { useState, useEffect, useRef, useCallback } from "react";

/**
 * @param {any} defaultValue
 * @param {((value: any) => boolean | Promise<boolean>) | null | undefined} validate
 * @param {boolean | null | undefined} focus
 * @param {boolean | undefined} isLoading
 * @param {boolean | undefined} readOnly
 * @returns {[
 *   {editing: boolean, valid: boolean, value: any, touched: boolean},
 *   import('react').Dispatch<import('react').SetStateAction<{editing: boolean, valid: boolean, value: any, touched: boolean}>>,
 *   (canEdit: boolean) => void,
 *   import('react').MutableRefObject<any>,
 *   () => void
 * ]}
 */
const useField = (defaultValue, validate, focus, isLoading, readOnly) => {
  const reference = useRef();
  const [loaded, setLoaded] = useState(false);

  const [field, setField] = useState({
    editing: false,
    valid: true,
    value: defaultValue,
    touched: false,
  });

  const handleEdit = useCallback(
    (canEdit) => {
      setField((prev) => ({
        ...prev,
        editing: canEdit && !readOnly,
      }));
    },
    [readOnly]
  );

  const markTouched = useCallback(() => {
    setField((prev) => (prev.touched ? prev : { ...prev, touched: true }));
  }, []);

  useEffect(() => {
    if (!isLoading && !defaultValue) {
      handleEdit(true);
    }
  }, [isLoading, defaultValue, handleEdit]);

  useEffect(() => {
    setLoaded(false);
  }, [isLoading]);

  useEffect(() => {
    if (focus) {
      reference.current?.focus();
    }
  }, [focus]);
  useEffect(() => {
    setField((prev) => ({
      ...prev,
      value: defaultValue,
      editing:
        !isLoading && !readOnly && (defaultValue === "" || defaultValue == null)
          ? true
          : prev.editing,
      touched:
        defaultValue === "" || defaultValue == null ? false : prev.touched,
    }));
  }, [defaultValue, isLoading, readOnly]);

  useEffect(() => {
    if (!loaded) {
      setField((prev) => ({
        ...prev,
        value: defaultValue,
      }));
      setLoaded(true);
    }
  }, [loaded, defaultValue]);

  useEffect(() => {
    const applyValidation = (validationResult) => {
      const next = Boolean(validationResult);
      if (field.valid !== next) {
        setField((prev) => ({ ...prev, valid: next }));
      }
    };
    if (validate) {
      Promise.resolve(validate(field.value)).then(applyValidation);
    }
  }, [field.value, field.valid, validate]);

  return [field, setField, handleEdit, reference, markTouched];
};

export default useField;
